import { useEffect, useRef, useState } from 'react';

export function useScrollReveal(threshold = 0.15, forceVisible = false) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(forceVisible);

  useEffect(() => {
    if (forceVisible) {
      setIsVisible(true);
      return;
    }
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold, rootMargin: '0px 0px -50px 0px' }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold, forceVisible]);

  return { ref, isVisible };
}

export function useCountUp(end: number, duration = 1200, start = 0, decimals = 0, forceVisible = false) {
  const [value, setValue] = useState(forceVisible ? end : start);
  const [isStarted, setIsStarted] = useState(forceVisible);
  const [pulse, setPulse] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (forceVisible) return;
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isStarted) {
          setIsStarted(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [isStarted, forceVisible]);

  useEffect(() => {
    if (!isStarted) return;

    let startTime: number | null = null;
    let animId: number;

    // Ease out quint - fast start, smooth landing
    const easeOutQuint = (x: number) => 1 - Math.pow(1 - x, 5);

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = easeOutQuint(progress);
      const current = start + (end - start) * eased;
      setValue(Number(current.toFixed(decimals)));

      if (progress < 1) {
        animId = requestAnimationFrame(animate);
      } else {
        setPulse(true);
        setTimeout(() => setPulse(false), 400);
      }
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [isStarted, end, duration, start, decimals]);

  return { ref, value, pulse };
}